import { createApp } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App.vue";
import Login from "./pages/Login.vue";
import Workspaces from "./pages/Workspaces.vue";
import Inbox from "./pages/Inbox.vue";
import {
  ensureSession,
  openWorkspace,
  pickWorkspace,
  session,
} from "./composables/session";
import { currentAddress } from "./composables/addresses";
import "@fontsource-variable/roboto";
import "./styles/tokens.css";
import "./styles/base.css";
import "./styles/chat.css";
import "./styles/styleguide.css";
import "./styles/journey.css";
import "@vue-flow/core/dist/style.css";
import "@vue-flow/core/dist/theme-default.css";
import "@vue-flow/controls/dist/style.css";

/** Screens inside the company open in this tab: the address never names it (decision 0016). */
const inWorkspace = { auth: true, workspace: true };
const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/", redirect: "/inbox" },
    { path: "/login", component: Login },
    { path: "/workspaces", component: Workspaces, meta: { auth: true } },
    { path: "/inbox/:id?", component: Inbox, meta: inWorkspace },
    {
      path: "/flows",
      component: () => import("./pages/Flows.vue"),
      meta: inWorkspace,
    },
    {
      path: "/flows/:id",
      component: () => import("./pages/FlowEditor.vue"),
      meta: inWorkspace,
    },
    {
      path: "/settings/:section?/:item?/:tab?",
      component: () => import("./pages/Settings.vue"),
      meta: inWorkspace,
    },
    {
      path: "/reports/:tab?",
      component: () => import("./pages/Reports.vue"),
      meta: inWorkspace,
    },
    {
      path: "/setup/:view?",
      component: () => import("./pages/Onboarding.vue"),
      meta: inWorkspace,
    },
    {
      path: "/campaigns/:id?/:edit(edit)?",
      component: () => import("./pages/Campaigns.vue"),
      meta: inWorkspace,
    },
    {
      path: "/contacts",
      component: () => import("./pages/Contacts.vue"),
      meta: inWorkspace,
    },
    {
      path: "/agenda",
      component: () => import("./pages/Agenda.vue"),
      meta: inWorkspace,
    },
    {
      path: "/platform/:section?/:item?/:tab?",
      component: () => import("./pages/Platform.vue"),
      meta: { auth: true, platform: true },
    },
    { path: "/invite/:token", component: () => import("./pages/Invite.vue") },
    {
      path: "/reset-password/:token",
      component: () => import("./pages/PasswordReset.vue"),
    },
    {
      path: "/data-deletion/:code",
      component: () => import("./pages/DataDeletion.vue"),
    },
    {
      path: "/security",
      component: () => import("./pages/SecuritySetup.vue"),
      meta: { auth: true },
    },
    // The UI library and the reference screens it links to, with demo data: Nuvvun's team only.
    {
      path: "/styleguide",
      component: () => import("./pages/Styleguide.vue"),
      meta: { auth: true, platform: true },
    },
    {
      path: "/demo/:page?/:section?",
      component: () => import("./pages/Workspace.vue"),
      meta: { auth: true, platform: true },
    },
    // Unknown addresses reach the guard first, so old ones can be translated before falling back.
    {
      path: "/:pathMatch(.*)*",
      name: "fallback",
      component: { render: () => null },
    },
  ],
});

/** `/settings/templates/new` and `/settings/templates` are the same section. */
const settingsSection = (path: string) =>
  path.startsWith("/settings/") ? path.split("/")[2] : null;

router.beforeEach(async (to, from) => {
  // The journey's guide stays beside a settings section while the member opens its items (a new
  // template, an assistant's page) and comes back; closing it on the same page drops it.
  const guide = from.query.guide;
  if (
    typeof guide === "string" &&
    to.query.guide === undefined &&
    to.path !== from.path &&
    settingsSection(to.path) &&
    settingsSection(to.path) === settingsSection(from.path)
  )
    return { path: to.path, query: { ...to.query, guide }, hash: to.hash };
  // Old addresses (/w/:slug/..., Portuguese names) move to the current ones.
  const moved = currentAddress(to.path, to.hash);
  if (moved)
    return {
      path: moved.path,
      hash: moved.hash,
      query: moved.slug ? { ...to.query, ws: moved.slug } : to.query,
      replace: true,
    };
  if (to.name === "fallback") return { path: "/inbox", replace: true };
  if (!to.meta.auth) return true;
  const ok = await ensureSession();
  if (!ok)
    return {
      path: "/login",
      query: to.fullPath !== "/workspaces" ? { next: to.fullPath } : {},
    };
  // Where the platform requires it, managers and above turn verification on first.
  if (session.me?.mfaSetupRequired && to.path !== "/security")
    return { path: "/security", query: { next: to.fullPath } };
  if (to.meta.platform && !session.me?.platformRole) return "/workspaces";
  if (to.meta.workspace) {
    // A link from an e-mail or another system says which company; it opens and leaves the address.
    const asked = typeof to.query.ws === "string" ? to.query.ws : null;
    if (asked) {
      if (session.me?.workspaces.some((w) => w.slug === asked))
        openWorkspace(asked);
      const { ws: _ws, ...query } = to.query;
      return { path: to.path, query, hash: to.hash, replace: true };
    }
    const slug = pickWorkspace();
    if (!slug) return { path: "/workspaces", query: { next: to.fullPath } };
    if (slug !== session.slug) openWorkspace(slug);
    return true;
  }
  // Arriving at the list goes straight in when there is nothing to choose; coming from inside the
  // app (switching company) always shows it.
  if (to.path === "/workspaces" && from.matched.length === 0) {
    const slug = pickWorkspace();
    if (slug) {
      openWorkspace(slug);
      return typeof to.query.next === "string" ? to.query.next : "/inbox";
    }
  }
  return true;
});

// Opened inside the DoneTalk app (a settings page from "Você"): the app has its own navigation,
// so the page drops the rail. The flag lasts for the tab, across the page's own links.
try {
  if (new URLSearchParams(location.search).get("embed") === "1")
    sessionStorage.setItem("donetalk.embed", "1");
  if (sessionStorage.getItem("donetalk.embed") === "1")
    document.documentElement.dataset.embed = "1";
} catch {
  // No storage (private mode): the page keeps its rail.
}

createApp(App).use(router).mount("#app");
